import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCheck, faCircle} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment-timezone'
import _ from 'lodash'
// Layouts
import Section from '../layout/section'
// Utils
import slugify from '../../utils/slugify'
// Styles
import styles from './votes-history.module.scss'

interface VoteRecord {
  _id: string
  category_name: string
  sub_category_name: string
  photo_name: string | null
  voted_choice: string
  voted_time: string
}

interface VotesHistoryProps {
  currentVoter: {
    votes?: VoteRecord[]
  } | null
  isOneVotePerDay?: boolean
}

const VotesHistory = ({
  currentVoter,
  isOneVotePerDay,
}: VotesHistoryProps) => {
  const votes = _.filter(currentVoter?.votes, vote => Boolean(vote?._id))

  const totalCounts = _.reduce(votes, (acc: Record<string, number>, vote) => {
    const key = `${vote.sub_category_name}|${vote.voted_choice}`
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const groupByFormattedTime = (vote: VoteRecord) => moment(vote.voted_time).format('YYYY-MM-DD')

  const timeGroupedVotes = _(votes)
    .groupBy(groupByFormattedTime)
    .map((timeGroup, votedTime) => ({
      votedTime,
      groups: _(timeGroup)
        .groupBy('category_name')
        .map((groups, groupName) => ({
          key: `${slugify(groupName)}-${timeGroup}`,
          groupName,
          categories: _(groups)
            .groupBy('sub_category_name')
            .map((questions, categoryName) => ({
              key: `${slugify(categoryName)}-${groupName}`,
              categoryName,
              votes: _(questions)
                .groupBy(v => `${v.photo_name}|${v.voted_choice}`)
                .map((_photoGroup, key) => {
                  const [photoName, votedChoice] = key.split('|')
                  return {
                    key: photoName,
                    photoName,
                    votedChoice,
                    totalVotes: totalCounts[`${categoryName}|${votedChoice}`],
                  }
                })
                .value(),
            }))
            .value(),
        }))
        .value(),
    }))
    .orderBy(['votedTime'], ['desc'])
    .value()

  const groupGroupedVotes = _(votes)
    .groupBy('category_name')
    .map((groupVotes, groupName) => ({
      key: groupName,
      groupName,
      votes: _.flatMap(groupVotes, vote => ({
        key: `${vote.sub_category_name}-${vote.voted_choice}`,
        questionName: vote.sub_category_name,
        photoName: vote.photo_name,
        votedChoice: vote.voted_choice,
        votedTime: vote.voted_time,
      })),
    }))
    .orderBy(['votedTime'], ['desc'])
    .value()

  if (timeGroupedVotes.length <= 0 && groupGroupedVotes.length <= 0) {
    return (
      <div className={styles['__no-items']}>
        <h3>You haven't cast any votes for this ballot</h3>
        <h4>Please return back once you have</h4>
      </div>
    )
  }

  return (
    <Section
      styleModifiers={[
        '--is-div',
        '--is-display-block',
        '--is-centered-horizontally',
      ]}
    >
      <div className={styles['votes-history']}>
        {isOneVotePerDay &&
          <ul className={styles['__times-list']}>
            {_.map(timeGroupedVotes, time => {
              const groupDate = moment(time.votedTime).format('YYYY-MM-DD')
              const formattedTime = moment(groupDate).format('dddd, MMMM Do YYYY')
              return (
                <li
                  key={time.votedTime}
                  className={styles['__times-list-item']}
                >
                  <div className={styles['__list-header']}>{formattedTime}</div>
                  <ul className={styles['__groups-list']}>
                    {_.map(time.groups, group => {
                      return (
                        <li
                          key={group.key}
                          className={styles['__groups-list-item']}
                          style={{marginTop: '0px'}}
                        >
                          {time.groups.length > 1 && <div className={styles['__list-subheader']}>{group.groupName}</div>}
                          <ul className={styles['__categories-list']}>
                            {_.map(group.categories, category => {
                              return (
                                <li
                                  key={category.key}
                                  className={styles['__categories-list-item']}
                                >
                                  <ul className={styles['__votes-list']}>
                                    { _.map(category.votes, vote => {
                                      return (
                                        <li
                                          key={vote.key}
                                          className={styles['__votes-list-item']}
                                        >
                                          <div className={styles['__link-contents']}>
                                            <div className={styles['__icon-container']}>
                                              <span className='fa-layers fa-3x'>
                                                <FontAwesomeIcon icon={faCircle} color='#47a87a' />
                                                <FontAwesomeIcon icon={faCheck} inverse transform='shrink-6' />
                                              </span>
                                            </div>
                                            <div className={styles['__text-container']}>
                                              <div className={styles['__question-name']}>{category.categoryName}</div>
                                              <div className={styles['__vote-name']}>
                                                {vote?.photoName}
                                                <span className={styles['__vote-count']}>
                                                  (Voted {vote?.totalVotes} {vote?.totalVotes > 1 ? 'times' : 'time'})
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </li>
                                      )
                                    }) }
                                  </ul>
                                </li>
                              )
                            })}
                          </ul>
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>
        }
        {!isOneVotePerDay &&
          <ul className={styles['__groups-list']}>
            {_.map(groupGroupedVotes, group => {
              return (
                <li
                  key={group.key}
                  className={styles['__groups-list-item']}
                >
                  {groupGroupedVotes.length > 1 && <div className={styles['__list-header']}>{group.groupName}</div>}
                  <ul className={styles['__votes-list']}>
                    { _.map(_.orderBy(group.votes, ['votedTime'], ['desc']), vote => {
                      return (
                        <li
                          key={vote.key}
                          className={styles['__votes-list-item']}
                        >
                          <div className={styles['__link-contents']}>
                            <div className={styles['__icon-container']}>
                              <span className='fa-layers fa-3x'>
                                <FontAwesomeIcon icon={faCircle} color='#47a87a' />
                                <FontAwesomeIcon icon={faCheck} inverse transform='shrink-6' />
                              </span>
                            </div>
                            <div className={styles['__text-container']}>
                              <div className={styles['__question-name']}>{vote.questionName}</div>
                              <div className={styles['__vote-name']}>{vote?.photoName}</div>
                            </div>
                          </div>
                        </li>
                      )
                    }) }
                  </ul>
                </li>
              )
            })}
          </ul>
        }
      </div>
    </Section>
  )
}

export default VotesHistory
